import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Quote, 
  Star, 
  Users, 
  Award, 
  ArrowRight,
  Calendar,
  MapPin,
  Heart,
  TrendingUp,
  CheckCircle
} from '../components/icons';

const GetInspired = () => {
  const [activeTab, setActiveTab] = useState('stories');

  const successStories = [
    {
      id: 1,
      title: 'Dr. Sarah Mwangi\'s Mercury-Free Transformation',
      location: 'Nairobi, Kenya',
      image: '/images/Dr. Sarah Mwangi\'s Mercury-Free Transformation.jpg',
      excerpt: 'How Dr. Mwangi transformed her practice with mercury-free materials and increased patient satisfaction by 40%.',
      fullStory: 'Dr. Sarah Mwangi had been practicing dentistry for over 15 years when she decided to make the switch to mercury-free materials. Through Dentopharma\'s comprehensive training program and ongoing support, she not only improved patient outcomes but also saw a 40% increase in patient satisfaction and a 25% growth in her practice revenue.',
      results: ['40% increase in patient satisfaction', '25% growth in practice revenue', '100% mercury-free practice'],
      category: 'Mercury-Free Dentistry'
    },
    {
      id: 2,
      title: 'Building a Modern Dental Clinic from Scratch',
      location: 'Dar es Salaam, Tanzania',
      image: '/images/Building a Modern Dental Clinic from Scratch.jpg',
      excerpt: 'Dr. James Kimaro shares how Dentopharma helped him establish a state-of-the-art dental clinic.',
      fullStory: 'Starting from a small practice, Dr. James Kimaro partnered with Dentopharma to build a comprehensive dental clinic. From equipment selection to staff training, Dentopharma provided end-to-end support, resulting in a modern facility that serves over 200 patients monthly.',
      results: ['200+ patients served monthly', 'Modern equipment setup', 'Comprehensive staff training'],
      category: 'Practice Development'
    },
    {
      id: 3,
      title: 'Community Health Impact in Rural Uganda',
      location: 'Kampala, Uganda',
      image: '/images/Community Health Impact in Rural Uganda.jpeg',
      excerpt: 'How Dr. Grace Nakato brought quality dental care to underserved communities.',
      fullStory: 'Dr. Grace Nakato used Dentopharma\'s mobile dental solutions to bring quality care to rural communities. Through strategic partnerships and affordable equipment, she established mobile clinics that now serve over 500 patients across 15 villages.',
      results: ['500+ patients served', '15 villages covered', 'Mobile clinic established'],
      category: 'Community Impact'
    }
  ];

  const testimonials = [
    {
      id: 1,
      name: 'Andrew Madembwe',
      title: 'Dental Professional',
      clinic: 'Dar es Salaam, Tanzania',
      image: `https://ui-avatars.com/api/?name=Andrew+Madembwe&background=0D56A6&color=fff&size=128&bold=true`,
      rating: 5,
      quote: 'Cention N restorative material is a replacement of Glass Ionomer cement and Amalgam restorative materials. Available in shade of A2.',
      results: 'Verified customer review'
    },
    {
      id: 2,
      name: 'Digna Adam',
      title: 'Dental Professional',
      clinic: 'Dar es Salaam, Tanzania',
      image: `https://ui-avatars.com/api/?name=Digna+Adam&background=0D56A6&color=fff&size=128&bold=true`,
      rating: 5,
      quote: 'Cention N what a filling material! Thank you guys for this material, I have used this material for almost two months now and non of my patient came back with complain.',
      results: 'Verified customer review'
    },
    {
      id: 3,
      name: 'Munira Bahraq',
      title: 'Dental Professional',
      clinic: 'Dar es Salaam, Tanzania',
      image: `https://ui-avatars.com/api/?name=Munira+Bahraq&background=0D56A6&color=fff&size=128&bold=true`,
      rating: 5,
      quote: 'Cention N - Excellent product quality and reliable service. Highly recommended for dental practices.',
      results: 'Verified customer review'
    }
  ];

  const achievements = [
    {
      icon: Users,
      number: '500+',
      label: 'Dental Professionals Trained',
      description: 'Comprehensive training programs across East Africa'
    },
    {
      icon: Award,
      number: '98%',
      label: 'Satisfaction Rate',
      description: 'From our training and support programs'
    },
    {
      icon: TrendingUp,
      number: '40%',
      label: 'Average Practice Growth',
      description: 'For practices using our integrated approach'
    },
    {
      icon: Heart,
      number: '10,000+',
      label: 'Patients Impacted',
      description: 'Through our partner practices'
    }
  ];

  const upcomingEvents = [
    {
      id: 1,
      title: 'Mercury-Free Dentistry Workshop',
      date: '2026-07-15',
      location: 'Dar es Salaam, Tanzania',
      description: 'Learn the latest techniques in mercury-free dental practices with hands-on Cention N training.',
      image: '/images/Events/Cention n phase 1/Cention-workshop_03.jpg',
      type: 'Workshop'
    },
    {
      id: 2,
      title: 'Digital Dentistry Conference',
      date: '2026-08-20',
      location: 'Nairobi, Kenya',
      description: 'Explore the future of digital dental technology and modern aesthetic workflows.',
      image: '/images/Events/Aesthetic Dentistry/Aesthetic-Dentistry_02.jpg',
      type: 'Conference'
    },
    {
      id: 3,
      title: 'Practice Management Seminar',
      date: '2026-09-10',
      location: 'Kampala, Uganda',
      description: 'Strategies for growing your dental practice and improving patient outcomes.',
      image: '/images/Events/KSI meeting/KSI-meeting_01.jpg',
      type: 'Seminar'
    }
  ];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section data-hero className="relative py-24 bg-gradient-to-br from-[#0D56A6] via-[#1a6bb8] to-[#8EC641] overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-15">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(255,255,255,0.2),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.15),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_40%_40%,rgba(255,255,255,0.1),transparent_70%)]" />
        </div>
        
        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{
              y: [0, -20, 0],
              rotate: [0, 5, 0],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-20 left-10 w-16 h-16 bg-white/10 rounded-full blur-sm"
          />
          <motion.div
            animate={{
              y: [0, 15, 0],
              rotate: [0, -3, 0],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
            className="absolute top-32 right-20 w-12 h-12 bg-white/10 rounded-full blur-sm"
          />
          <motion.div
            animate={{
              y: [0, -10, 0],
              rotate: [0, 2, 0],
            }}
            transition={{
              duration: 7,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
            className="absolute bottom-20 left-1/4 w-8 h-8 bg-white/10 rounded-full blur-sm"
          />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center text-white"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent leading-tight"
            >
              Dentists Transforming Care Across East Africa
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl md:text-2xl text-white/95 max-w-4xl mx-auto mb-4 leading-relaxed font-light"
            >
              Discover success stories, testimonials, and achievements from dental professionals 
              who have transformed their practices with Dentopharma.
            </motion.p>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-lg text-white/80 max-w-2xl mx-auto mb-12"
            >
              Join hundreds of dental professionals who are advancing dentistry across East Africa
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            >
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center bg-white text-[#0D56A6] px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Join Our Community
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="/events"
                className="group inline-flex items-center justify-center border-2 border-white/80 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-[#0D56A6] hover:scale-105 transition-all duration-300 backdrop-blur-sm hover:shadow-xl"
              >
                <Calendar size={20} className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                View Events
              </Link>
            </motion.div>

            {/* Stats Preview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto"
            >
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">500+</div>
                <div className="text-sm text-white/80">Professionals Trained</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">98%</div>
                <div className="text-sm text-white/80">Satisfaction Rate</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">40%</div>
                <div className="text-sm text-white/80">Practice Growth</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">10K+</div>
                <div className="text-sm text-white/80">Patients Impacted</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="py-8 bg-gray-50">
        <div className="container-custom">
          <div className="flex justify-center">
            <div className="flex space-x-1 bg-white rounded-lg p-1 shadow-sm">
              <button
                onClick={() => setActiveTab('stories')}
                className={`px-6 py-3 rounded-md font-medium transition-colors duration-300 ${
                  activeTab === 'stories'
                    ? 'bg-[#0D56A6] text-white'
                    : 'text-gray-600 hover:text-[#0D56A6]'
                }`}
              >
                Success Stories
              </button>
              <button
                onClick={() => setActiveTab('testimonials')}
                className={`px-6 py-3 rounded-md font-medium transition-colors duration-300 ${
                  activeTab === 'testimonials'
                    ? 'bg-[#0D56A6] text-white'
                    : 'text-gray-600 hover:text-[#0D56A6]'
                }`}
              >
                Testimonials
              </button>
              <button
                onClick={() => setActiveTab('events')}
                className={`px-6 py-3 rounded-md font-medium transition-colors duration-300 ${
                  activeTab === 'events'
                    ? 'bg-[#0D56A6] text-white'
                    : 'text-gray-600 hover:text-[#0D56A6]'
                }`}
              >
                Upcoming Events
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories Tab */}
      {activeTab === 'stories' && (
        <section className="py-20 bg-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Success Stories
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Real stories from dental professionals who have transformed their practices 
                and improved patient outcomes with Dentopharma.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {successStories.map((story, index) => (
                <motion.article
                  key={story.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={story.image}
                      alt={story.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/images/blog/default-blog.jpg';
                      }}
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <span className="px-3 py-1 bg-[#0D56A6]/10 text-[#0D56A6] text-sm font-medium rounded-full">
                        {story.category}
                      </span>
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin size={16} className="mr-1" />
                        {story.location}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#0D56A6] transition-colors duration-300">
                      {story.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {story.excerpt}
                    </p>
                    <div className="space-y-2 mb-4">
                      {story.results.map((result, idx) => (
                        <div key={idx} className="flex items-center text-sm text-gray-600">
                          <CheckCircle size={16} className="text-[#8EC641] mr-2 flex-shrink-0" />
                          {result}
                        </div>
                      ))}
                    </div>
                    <button className="w-full text-[#0D56A6] hover:text-[#8EC641] font-medium transition-colors duration-300">
                      Read Full Story
                    </button>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Tab */}
      {activeTab === 'testimonials' && (
        <section className="py-20 bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What Our Partners Say
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Hear from dental professionals who have experienced the Dentopharma difference.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-center mb-4">
                    {testimonial.image ? (
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover mr-4"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/images/blog/default-blog.jpg';
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#0D56A6] flex items-center justify-center mr-4">
                        <span className="text-white font-semibold text-lg">
                          {testimonial.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                      <p className="text-sm text-gray-600">{testimonial.title}</p>
                      <p className="text-sm text-gray-500">{testimonial.clinic}</p>
                    </div>
                  </div>
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={16} className="text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <Quote size={24} className="text-[#0D56A6] mb-3" />
                  <p className="text-gray-700 mb-4 italic">
                    "{testimonial.quote}"
                  </p>
                  <div className="text-sm font-medium text-[#8EC641]">
                    {testimonial.results}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Events Tab */}
      {activeTab === 'events' && (
        <section className="py-20 bg-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Upcoming Events
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Join our workshops, conferences, and training sessions to enhance your skills 
                and stay updated with the latest in dentistry.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcomingEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
                >
                  <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-[#0D56A6] to-[#062f63]">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-[#8EC641] text-white text-xs font-bold rounded-full shadow">
                        {event.type}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#0D56A6] transition-colors duration-300">
                      {event.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {event.description}
                    </p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar size={16} className="mr-2" />
                        {formatDate(event.date)}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin size={16} className="mr-2" />
                        {event.location}
                      </div>
                    </div>
                    <button className="w-full bg-[#0D56A6] text-white py-2 px-4 rounded-lg hover:bg-[#0A4A8F] transition-colors duration-300">
                      Register Now
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Achievements Section */}
      <section className="py-20 bg-[#0D56A6]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Our Impact
            </h2>
            <p className="text-lg text-white/90 max-w-3xl mx-auto">
              Numbers that reflect our commitment to transforming dentistry across East Africa.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center text-white"
              >
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <achievement.icon size={32} className="text-white" />
                </div>
                <div className="text-3xl font-bold mb-2">{achievement.number}</div>
                <div className="text-lg font-semibold mb-2">{achievement.label}</div>
                <div className="text-sm text-white/80">{achievement.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Join hundreds of dental professionals who have transformed their practices 
              with Dentopharma's comprehensive support and quality products.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-[#0D56A6] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#0A4A8F] transition-colors duration-300"
              >
                Get Started Today
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <Link
                to="/events"
                className="inline-flex items-center justify-center border-2 border-[#0D56A6] text-[#0D56A6] px-8 py-4 rounded-lg font-semibold hover:bg-[#0D56A6] hover:text-white transition-colors duration-300"
              >
                View Training Programs
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GetInspired;
